import { useStepperContext } from '@folio/components';
import { useAssertedParams } from '@folio/lib';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  clientWithFork,
  useAssetControllerCreateAsset,
  viewSpecificControllerListPolicyAssetsQueryKey,
  viewSpecificControllerListPolicyAssetsQueryOptions,
} from 'korint-sdk';
import { Loader2Icon } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export function EmptyAssetFunnelModule() {
  const { t } = useTranslation();
  const stepper = useStepperContext();
  const queryClient = useQueryClient();

  const { policyId, branchId } = useAssertedParams();

  const {
    mutateAsync: createAsset,
    isPending: isCreatingAsset,
    isSuccess: isCreatedAsset,
    isError: isCreatingAssetError,
  } = useAssetControllerCreateAsset({
    client: { client: clientWithFork },
    mutation: {
      onSuccess: async () => {
        // The next step reads this same list to pick create-vs-update; advancing on a
        // stale empty list makes it create a second, field-less asset.
        await queryClient.invalidateQueries({
          queryKey: viewSpecificControllerListPolicyAssetsQueryKey(policyId),
        });
        stepper.next();
      },
    },
  });

  const {
    data: existingAssets,
    isPending: isLoadingAssets,
    isError: isLoadingAssetsError,
  } = useQuery({
    ...viewSpecificControllerListPolicyAssetsQueryOptions(policyId, {
      branchId,
    }),
  });

  useEffect(() => {
    if (isLoadingAssets) {
      return;
    }

    if (isLoadingAssetsError || isCreatingAssetError) {
      return;
    }

    // A create owns the transition from here — `onSuccess` advances.
    if (isCreatingAsset || isCreatedAsset) {
      return;
    }

    if (existingAssets?.data.length === 0) {
      createAsset({
        params: { branchId },
        data: {
          policyId,
        },
      });
      return;
    }
    stepper.next();
  }, [
    existingAssets,
    isLoadingAssets,
    isCreatingAsset,
    isCreatedAsset,
    branchId,
    policyId,
    stepper,
    isLoadingAssetsError,
    isCreatingAssetError,
  ]);

  if (isCreatingAssetError || isLoadingAssetsError) {
    return <div>{t('emptyAssetFunnel.creationError')}</div>;
  }

  return <Loader2Icon className="size-8 animate-spin" />;
}
